import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Container,
  useMediaQuery,
  Divider,
  Chip,
  Grid2,
  Step,
  StepLabel,
  Stepper,
  Stack,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ChatIcon from '@mui/icons-material/Chat';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import QueryBuilderIcon from '@mui/icons-material/QueryBuilder';
import HelpIcon from '@mui/icons-material/Help';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { useTheme, Theme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { format, addBusinessDays, isValid } from 'date-fns';
import { RootState } from '../../../core/store';
import GradientButton from '../../../core/theme/GradientBadge';
import { UserStatus, WAITLIST_URL } from '../../../core/constant/constant';
import { setCompleteEnrollment } from '../../../core/store/slices/basicInfoSlice';
interface ConfirmationHeaderProps {
  isLargeScreen: boolean;
  hasPartner: boolean;
}

interface ApplicationStatusProps {
  applicationDate: string;
  theme: Theme;
}

interface NextStepsSectionProps {
  estimatedCompletionDate: string;
  theme: Theme;
  hasPartner: boolean;
}

interface NextStepItemProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  footnote?: React.ReactNode;
}

interface SupportSectionProps {
  theme: Theme;
  navigate: (path: string, options?: { replace: boolean }) => void;
}

interface ProgressTrackerProps {
  theme: Theme;
}

const SuccessIcon: React.FC = () => {
  const theme = useTheme();

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
      <Box
        sx={{
          bgcolor: 'rgba(76, 175, 80, 0.1)',
          borderRadius: '50%',
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CheckCircleOutlineIcon
          sx={{
            fontSize: { xs: 50, md: 60 },
            color: theme.palette.success.main,
          }}
        />
      </Box>
    </Box>
  );
};

const ConfirmationHeader: React.FC<ConfirmationHeaderProps> = ({ isLargeScreen, hasPartner }) => {
  const theme = useTheme();
  return (
    <Box sx={{ textAlign: 'center', mb: 4 }}>
      <Typography
        variant={isLargeScreen ? 'h3' : 'h4'}
        component='h1'
        color={theme.palette.primary[600]}
        sx={{
          mb: 1,
          fontWeight: 600,
        }}
      >
        Welcome to the club 🎉
      </Typography>
      <Typography
        variant='h6'
        color={theme.palette.primary[800]}
        sx={{ maxWidth: '700px', mx: 'auto', lineHeight: 1.6 }}
      >
        Thank you for choosing MindRight Health for your mental wellness journey.
      </Typography>
      <Typography
        variant='h6'
        color={theme.palette.primary[800]}
        sx={{ maxWidth: '700px', mx: 'auto', lineHeight: 1.6 }}
      >
        We're thrilled to have you join our community
      </Typography>
    </Box>
  );
};

const ApplicationStatus: React.FC<ApplicationStatusProps> = ({ applicationDate, theme }) => (
  <Paper
    elevation={0}
    sx={{
      bgcolor: theme.palette.grey[50],
      p: { xs: 3, sm: 3 },
      borderRadius: '8px',
      mb: 4,
      display: 'flex',
      flexDirection: { xs: 'column', sm: 'row' },
      alignItems: 'center',
      gap: { xs: 2, sm: 0 },
      justifyContent: 'space-around',
      textAlign: 'center',
      border: `1px dashed ${theme.palette.divider}`,
      position: 'relative',
      overflow: 'hidden',
    }}
  >
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '4px',
        background: `linear-gradient(90deg, ${theme.palette.primary[400]}, ${theme.palette.primary[600]})`,
        opacity: 0.7,
      }}
    />

    <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 0.5 }}>
        Submitted On
      </Typography>
      <Typography
        variant='subtitle1'
        sx={{
          fontSize: { xs: '0.9rem', sm: '1rem' },
          fontWeight: 500,
        }}
      >
        {applicationDate}
      </Typography>
    </Box>

    <Divider
      orientation='vertical'
      flexItem
      sx={{
        display: { xs: 'none', sm: 'block' },
        mx: { sm: 2 },
        height: '50px',
      }}
    />
    <Divider
      sx={{
        display: { xs: 'block', sm: 'none' },
        width: '80%',
        my: 1,
      }}
    />

    <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 0.5 }}>
        Status
      </Typography>
      <Chip
        label='Finalizing Your Membership'
        color='primary'
        size='small'
        sx={{
          fontSize: { xs: '0.75rem', sm: '0.8125rem' },
          fontWeight: 500,
          py: 0.5,
          px: 0.5,
        }}
        icon={<QueryBuilderIcon style={{ fontSize: '0.875rem' }} />}
      />
    </Box>
  </Paper>
);

const NextStepsSection: React.FC<NextStepsSectionProps> = ({
  estimatedCompletionDate,
  theme,
  hasPartner,
}) => {
  const isCompletionDatePassed = useMemo(() => {
    if (estimatedCompletionDate === 'Date not available') {
      return false;
    }

    try {
      const [, monthDay] = estimatedCompletionDate.split(', ');
      const [month, day] = monthDay.split(' ');

      const currentYear = new Date().getFullYear();
      const completionDate = new Date(`${month} ${day}, ${currentYear}`);

      return completionDate < new Date();
    } catch (error) {
      console.error('Error checking if completion date passed:', error);
      return false;
    }
  }, [estimatedCompletionDate]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 3, md: 3 },
        mb: 4,
        borderRadius: '8px',
        border: `1px solid ${theme.palette.primary.light}`,
        bgcolor: theme.palette.background.default || 'rgba(25, 118, 210, 0.05)',
        position: 'relative',
        width: '100%',
        minHeight: { xs: 'auto' },
      }}
    >
      <Typography
        variant='h5'
        sx={{
          mb: { xs: 3, sm: 3 },
          fontWeight: 600,
          color: theme.palette.primary.dark,
          display: 'flex',
          alignItems: 'center',
          fontSize: { xs: '1.1rem', sm: '1.25rem' },
        }}
      >
        <QueryBuilderIcon sx={{ mr: 1 }} /> What happens next:
      </Typography>

      {hasPartner ? (
        <>
          {/* <NextStepItem
            icon={<NotificationsIcon sx={{ fontSize: 28, color: theme.palette.primary.main }} />}
            title='Application Review'
            description='Our team will review your application within 3 business days'
            footnote={
              <Typography variant='body2' sx={{ mt: 0.5, display: 'flex', alignItems: 'center' }}>
                <QueryBuilderIcon
                  sx={{ fontSize: 16, mr: 0.5, color: theme.palette.text.secondary }}
                />
                {isCompletionDatePassed ? (
                  <span>
                    <b>Estimated completion has passed. We will reach out soon.</b>
                  </span>
                ) : (
                  <span>
                    Estimated completion: <b>{estimatedCompletionDate}</b>
                  </span>
                )}
              </Typography>
            }
          /> */}

          <NextStepItem
            icon={<ChatIcon sx={{ fontSize: 28, color: theme.palette.secondary.main }} />}
            title='Confirmation Message'
            description="You'll receive a text within 3 business days when your membership is finalized."
          />
        </>
      ) : (
        <Box
          sx={{
            px: { xs: 0, sm: 2 },
            py: { xs: 2, sm: 4 },
          }}
        >
          <Typography
            variant='body1'
            sx={{
              mb: 3,
              lineHeight: 1.6,
              fontSize: { xs: '0.95rem', sm: '1rem' },
            }}
          >
            Wheekly Wellness
          </Typography>
          <Typography
            variant='body2'
            sx={{
              mb: 3,
              lineHeight: 1.6,
              fontSize: { xs: '0.95rem', sm: '1rem' },
            }}
          >
            You'll start receiving weekly mental wellness tips and content to your phone
          </Typography>

          {hasPartner && (
            <NextStepItem
              icon={<ChatIcon sx={{ fontSize: 28, color: theme.palette.secondary.main }} />}
              title='Text Notification'
              description="You'll receive a text from us about the matching process within a week."
            />
          )}
        </Box>
      )}
    </Paper>
  );
};

const NextStepItem: React.FC<NextStepItemProps> = ({ icon, title, description, footnote }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      mb: footnote ? { xs: 3, sm: 4 } : 0,
      position: 'relative',
      pl: { xs: 0, sm: 3 },
    }}
  >
    <Box
      sx={{
        display: 'flex',
        mr: 2,
        minWidth: { xs: '28px', sm: '28px' },
        alignItems: 'flex-start',
        justifyContent: 'center',
      }}
    >
      {icon}
    </Box>
    <Box sx={{ flex: 1 }}>
      <Typography
        variant='subtitle1'
        sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.1rem' } }}
      >
        {title}
      </Typography>
      <Typography
        variant='body1'
        color='text.secondary'
        sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
      >
        {description}
      </Typography>
      {footnote && <Box sx={{ mt: 1 }}>{footnote}</Box>}
    </Box>
  </Box>
);

const SupportSection: React.FC<SupportSectionProps> = ({ theme }) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 3, md: 3 },
      borderRadius: '8px',
      border: `1px solid ${theme.palette.divider}`,
      bgcolor: theme.palette.background.paper,
      mb: 3,
      position: 'relative',
      overflow: 'hidden',
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': {
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        transform: 'translateY(-2px)',
      },
    }}
  >
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        right: 0,
        width: '100px',
        height: '100px',
        background: `radial-gradient(circle at top right, ${theme.palette.primary.light}22, transparent 70%)`,
        opacity: 0.7,
      }}
    />
    <Typography
      variant='h6'
      sx={{
        mb: 2,
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        fontSize: { xs: '1rem', sm: '1.125rem' },
        position: 'relative',
      }}
    >
      <HelpIcon sx={{ mr: 1, fontSize: '1.2rem', color: theme.palette.secondary.main }} /> Need
      support while you wait?
    </Typography>
    <Typography variant='body2' color='text.secondary' sx={{ mb: 2, position: 'relative' }}>
      Check out this list of trusted national resources.
    </Typography>
    <Box sx={{ display: 'flex', gap: 2, position: 'relative', flexWrap: 'wrap' }}>
      <Button
        variant='contained'
        // color='primary'
        size='small'
        sx={{
          py: 1,
          px: 2,
          borderRadius: '8px',
          textTransform: 'none',
          fontWeight: 500,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          '&:hover': {
            boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
            background: theme.palette.primary[500],
          },
          background: theme.palette.primary[400],
        }}
        endIcon={<ArrowForwardIcon />}
        component='a'
        href='https://www.mindright.io/resources'
        target='_blank'
        rel='noopener noreferrer'
      >
        Resources
      </Button>
      <Button
        variant='outlined'
        size='small'
        sx={{
          py: 1,
          px: 2,
          borderRadius: '8px',
          textTransform: 'none',
          fontWeight: 500,
          borderColor: theme.palette.primary[400],
          color: theme.palette.primary[400],
          '&:hover': {
            borderColor: theme.palette.primary[500],
            backgroundColor: 'rgba(92, 90, 240, 0.04)',
          },
        }}
        endIcon={<ArrowForwardIcon />}
        component='a'
        href={WAITLIST_URL}
        target='_blank'
        rel='noopener noreferrer'
      >
        Join Waitlist
      </Button>
    </Box>
  </Paper>
);

const ProgressTracker: React.FC<ProgressTrackerProps> = ({ theme }) => {
  // 'Under Review', 'Membership Approved',
  const steps = ['Submitted', 'Finalizing Membership'];

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 3, md: 3 },
        borderRadius: '8px',
        border: `1px solid ${theme.palette.primary.light}`,
        bgcolor: 'rgba(25, 118, 210, 0.03)',
        mb: 4,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '4px',
          height: '100%',
          bgcolor: theme.palette.primary.main,
        }}
      />

      <Typography
        variant='h6'
        sx={{
          mb: 3,
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          fontSize: { xs: '1rem', sm: '1.125rem' },
          color: theme.palette.primary.dark,
        }}
      >
        <TaskAltIcon sx={{ mr: 1, fontSize: '1.2rem' }} /> Your Application Progress
      </Typography>

      <Stepper
        activeStep={1}
        alternativeLabel
        sx={{
          mb: 2,
          '& .MuiStepLabel-root .Mui-completed': {
            color: theme.palette.success.main,
          },
          '& .MuiStepLabel-root .Mui-active': {
            color: theme.palette.primary.main,
          },
        }}
      >
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* <Typography variant='body2' color='text.secondary' sx={{ textAlign: 'center', mt: 2 }}>
        We'll keep you updated as your application progresses through each stage.
      </Typography> */}
    </Paper>
  );
};
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
const ApplicationReceived: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'));
  const basicInfo = useSelector((state: RootState) => state.basicInfo);
  const personalDetails = useSelector((state: RootState) => state.personalDetails);
  const hasPartner =
    basicInfo?.partnerId !== 'NONE' || basicInfo.currentStatus === UserStatus.PENDING_MATCHING;
  const formattedApplicationDate = useMemo(() => {
    if (!basicInfo.userEnrollment?.applicationEndDate) {
      return 'Date not available';
    }

    try {
      const appDate = new Date(basicInfo.userEnrollment.applicationEndDate);

      if (!isValid(appDate)) {
        return 'Date not available';
      }

      return format(appDate, "EEEE, MMMM d, yyyy 'at' hh:mm a");
    } catch (error) {
      console.error('Error formatting application date:', error);
      return 'Date not available';
    }
  }, [basicInfo.userEnrollment?.applicationEndDate]);

  const estimatedCompletionDate = useMemo(() => {
    if (!basicInfo.userEnrollment?.applicationEndDate) {
      return 'Date not available';
    }

    try {
      const appDate = new Date(basicInfo.userEnrollment.applicationEndDate);

      if (!isValid(appDate)) {
        return 'Date not available';
      }

      const completionDate = addBusinessDays(appDate, 3);
      return format(completionDate, 'EEEE, MMM d');
    } catch (error) {
      console.error('Error calculating business days:', error);
      return 'Date not available';
    }
  }, [basicInfo.userEnrollment?.applicationEndDate]);
  const handleGoToHome = () => {
    navigate('/home');
    dispatch(setCompleteEnrollment(false));
  };
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: theme.palette.background.default,
        pt: 2,
        pb: 6,
        background: `linear-gradient(to bottom, ${theme.palette.background.default}, ${theme.palette.grey[50]})`,
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          maxWidth: { xs: '100%', sm: '100%', md: '1000px', lg: '1200px' },
          px: { xs: 2, sm: 3, md: 4 },
          mx: 'auto',
        }}
      >
        <Paper
          elevation={isLargeScreen ? 1 : 0}
          sx={{
            p: { xs: 3, sm: 4, md: 4 },
            pt: { xs: 4, sm: 5 },
            borderRadius: { xs: '12px', md: '16px' },
            boxShadow: isLargeScreen ? '0 4px 20px rgba(0, 0, 0, 0.05)' : 'none',
            mb: 4,
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '8px',
              background: `linear-gradient(90deg, ${theme.palette.primary[400]}, ${theme.palette.primary[600]})`,
              opacity: 0.8,
            }}
          />

          <SuccessIcon />
          {hasPartner ? (
            <GradientButton
              colors={['#F3AB40', '#EB6A34']}
              style={{ position: 'absolute', top: 20, right: 10 }}
              textcolor={'#000'}
              disabled
              size='medium'
            >
              VIP Member
            </GradientButton>
          ) : (
            <GradientButton
              colors={['#952BB3', '#662075']}
              style={{ position: 'absolute', top: 20, right: 10 }}
              textcolor={'#fff'}
              disabled
              size='medium'
            >
              Community Member
            </GradientButton>
          )}

          <ConfirmationHeader isLargeScreen={isLargeScreen} hasPartner={hasPartner} />

          {hasPartner && (
            <ApplicationStatus applicationDate={formattedApplicationDate} theme={theme} />
          )}

          {hasPartner ? (
            <Grid2 container spacing={4}>
              <Grid2 size={{ xs: 12, md: 7 }}>
                <NextStepsSection
                  estimatedCompletionDate={estimatedCompletionDate}
                  theme={theme}
                  hasPartner={hasPartner}
                />
                <SupportSection theme={theme} navigate={navigate} />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 5 }}>
                <ProgressTracker theme={theme} />
              </Grid2>
            </Grid2>
          ) : (
            <NextStepsSection
              estimatedCompletionDate={estimatedCompletionDate}
              theme={theme}
              hasPartner={hasPartner}
            />
          )}
          <Stack alignItems={'center'}>
            {!hasPartner && (
              <Button onClick={handleGoToHome} variant='contained'>
                Go to Dashboard
              </Button>
            )}
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};

export default ApplicationReceived;
